import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, CircleAlert, CircleDashed, Clock, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useLearnerCurriculumProgress,
  useLearnerModuleProgress,
  moduleState,
  MODULE_STATE_LABEL,
  type ModuleProgressRow,
  type ModuleState,
} from "@/hooks/useLearnerCurriculumProgress";
import { CurriculumProgressSummaryCard } from "./CurriculumProgressSummaryCard";

interface Props {
  curriculumId: string;
  /**
   * Set false when the caller already renders CurriculumProgressSummaryCard
   * itself (CurriculumDetail does, so the certificate CTA can sit beside it).
   */
  showSummary?: boolean;
  title?: string;
}

const STATE_ICON: Record<ModuleState, typeof CheckCircle2> = {
  complete: CheckCircle2,
  ready: CircleAlert,
  in_progress: Clock,
  available: CircleDashed,
  locked: Lock,
  not_assigned: Lock,
};

const STATE_STYLE: Record<ModuleState, string> = {
  complete: "text-green-600",
  ready: "text-amber-600",
  in_progress: "text-blue-600",
  available: "text-muted-foreground",
  locked: "text-muted-foreground/60",
  not_assigned: "text-muted-foreground/60",
};

function ModuleRow({ row }: { row: ModuleProgressRow }) {
  const state = moduleState(row);
  const Icon = STATE_ICON[state];
  const dimmed = state === "locked" || state === "not_assigned";

  return (
    <li className="flex items-start gap-3 py-2">
      <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", STATE_STYLE[state])} />

      <div className="min-w-0 flex-1">
        <div className={cn("text-sm truncate", dimmed && "text-muted-foreground")}>
          {row.moduleTitle}
        </div>

        <div className="text-xs text-muted-foreground">
          {MODULE_STATE_LABEL[state]}
          {/* Only show item counts when there is something to count. 34% of
              modules have no gradeable items, so a bare "0 of 0" would be noise. */}
          {row.itemsTotal > 0 && state !== "complete" && (
            <> · {row.itemsDone} of {row.itemsTotal} tasks done</>
          )}
        </div>
      </div>

      {state === "ready" && (
        <Badge variant="default" className="shrink-0 text-xs">
          Finish it
        </Badge>
      )}
    </li>
  );
}

/**
 * Full progress detail for one curriculum: headline bar plus a per-course
 * checklist of every module and its state.
 *
 * Note module TITLES are shown rather than "Module N of M". In production the
 * modules at sequence_order 1-4 are titled "Task 01..04" and 5-8 are
 * "Module 1..4", so a positional label would contradict what the learner reads
 * on screen.
 */
export function CurriculumProgressPanel({
  curriculumId,
  showSummary = true,
  title = "Your progress",
}: Props) {
  const { data: summaries, isLoading: loadingSummary } =
    useLearnerCurriculumProgress();
  const { data: modules, isLoading: loadingModules } =
    useLearnerModuleProgress(curriculumId);

  const summary = summaries?.find((s) => s.curriculumId === curriculumId);

  if (loadingSummary || loadingModules) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-2 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </CardContent>
      </Card>
    );
  }

  // Not enrolled, or nothing published yet — say so rather than showing 0%.
  if (!summary || !modules || modules.length === 0) {
    return null;
  }

  // Group by course, preserving the order the RPC returned.
  const byCourse = new Map<string, { title: string; rows: ModuleProgressRow[] }>();
  for (const row of modules) {
    const entry = byCourse.get(row.courseId);
    if (entry) {
      entry.rows.push(row);
    } else {
      byCourse.set(row.courseId, { title: row.courseTitle, rows: [row] });
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Your progress</CardTitle>
      </CardHeader>

      <CardContent className="space-y-5">
        {showSummary && (
          <CurriculumProgressSummaryCard summary={summary} showTitle={false} />
        )}

        {Array.from(byCourse.entries()).map(([courseId, course]) => {
          const done = course.rows.filter((r) => r.isComplete).length;
          const pct = Math.round(
            (course.rows.reduce((sum, r) => sum + r.fraction, 0) /
              Math.max(course.rows.length, 1)) *
              100
          );

          return (
            <div key={courseId} className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-sm font-medium">{course.title}</div>
                <div className="text-xs text-muted-foreground tabular-nums shrink-0">
                  {done}/{course.rows.length}
                </div>
              </div>

              <Progress value={pct} className="h-1" />

              <ul className="divide-y">
                {course.rows.map((row) => (
                  <ModuleRow key={row.moduleId} row={row} />
                ))}
              </ul>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
